import * as Ably from 'ably'
import { useEffect, useState } from 'react'
import { fetchApiJson } from '@vend/utilities'

import {
  clearAblyToken,
  clearChannelName,
  getAblyToken,
  getChannelName as getCachedChannelName,
  setAblyToken,
  setChannelName as setCachedChannelName,
} from '../utils/localStorage'
import { isTokenValid } from '../utils/token'
import { CFD_TYPE } from '../constants'

interface SessionListResponse {
  /** The register's current shared channel, or `null` when there are no active sessions. */
  channelName: string | null
  /** A fresh register-scoped Ably token, or `null` when there are no active sessions. */
  ably: Ably.TokenDetails | null
}

const buildSessionListUrl = (registerId: string) =>
  `/api/1.0/cfd/session/list?${new URLSearchParams({ registerId }).toString()}`

interface UseByodRealtimeClientResult {
  client: Ably.Realtime | null
  channelName: string | null
}

/**
 * - Creates an Ably `Realtime` client with an `authCallback` that first
 *   checks the localStorage cache. If the cached token is still valid it is
 *   handed straight to Ably; otherwise the cache is cleared, a fresh session
 *   is fetched from `/api/1.0/cfd/session/list`, and the new token +
 *   channel name are written back to the cache.
 * - Extracts `channelName` from the same response
 * - Returns `{ client, channelName }`
 */
export const useByodRealtimeClient = (
  registerId: string
): UseByodRealtimeClientResult => {
  const [client, setClient] = useState<Ably.Realtime | null>(null)
  const [channelName, setChannelName] = useState<string | null>(null)

  // Create / destroy the BYOD Ably client.
  useEffect(() => {
    setChannelName(null)
    setClient(null)

    if (!registerId) {
      return
    }

    // Guards async setState calls (from `authCallback`'s cache-hit branch
    // and `fetchApiJson().then(...)`) against firing after the effect has
    // been torn down.
    let cancelled = false

    const authCallback: NonNullable<Ably.AuthOptions['authCallback']> = (
      _params,
      done
    ) => {
      const cachedToken = getAblyToken(CFD_TYPE.BYOD, registerId)
      if (cachedToken && isTokenValid(cachedToken)) {
        if (!cancelled) {
          setChannelName(getCachedChannelName(CFD_TYPE.BYOD, registerId))
        }
        done(null, cachedToken)
        return
      }

      clearAblyToken(CFD_TYPE.BYOD, registerId)
      clearChannelName(CFD_TYPE.BYOD, registerId)

      fetchApiJson(buildSessionListUrl(registerId), { method: 'GET' })
        .then((response: SessionListResponse) => {
          if (!cancelled) {
            setChannelName(response?.channelName ?? null)
          }

          if (!response?.ably) {
            done('No active CFD session for register', null)
            return
          }

          setAblyToken(CFD_TYPE.BYOD, registerId, response.ably)
          if (response.channelName) {
            setCachedChannelName(
              CFD_TYPE.BYOD,
              registerId,
              response.channelName
            )
          }

          done(null, response.ably)
        })
        .catch((e: unknown) => {
          done(e instanceof Error ? e.message : String(e), null)
        })
    }

    const ablyClient = new Ably.Realtime({
      authCallback,
      closeOnUnload: true,
    })
    setClient(ablyClient)

    return () => {
      cancelled = true
      ablyClient.close()
      setClient(null)
      setChannelName(null)
    }
  }, [registerId])

  return { client, channelName }
}
